import { useFlag, useVariant } from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { update } from '../../redux/features/config/config.slice';
import { setAuthRefactorFeatureFlag } from '../../utils/session-storage';
import { setAgentInactivityFeatureFlag } from '../../utils/session-storage';
import {
  AGENT_CART_FLAG_NAME,
  DO_NOT_UPSELL_IN_EXPRESS_MODE_FLAG_NAME,
  SESSION_END_ORDER_ACTION_CHANGES_FLAG_NAME,
  SUPPRESS_UPSELL_FLAG_NAME,
  TIMER_RESET_ON_CART_UPDATE_FLAG_NAME,
  UNRESOLVED_ITEMS_FLAG_NAME,
  HITL_3_0_FLAG_NAME,
  HITL_EXPRESS_TIMER_FLAG_NAME,
  AGENT_RESPONSE_TIME_FLAG_NAME,
  DEFAULT_AGENT_RESPONSE_TIME_LIMIT_SECONDS,
  MENU_MODULE_ERROR_UI_FLAG_NAME,
  ORDER_ERROR_OMNIBAR_PROMPT_FLAG_NAME,
  SECONDARY_ASR_ENABLED_FLAG_NAME,
  ORDER_CHECK_RETRY_FLAG_NAME,
  DEFAULT_ORDER_CHECK_TIMEOUT_SECONDS,
  ORDER_STATUS_BANNER_FLAG_NAME,
  MASK_ORDER_PENDING_ERROR_FLAG_NAME,
  ISSUE_REPORTING_FLAG_NAME,
  AUTOFILL_FINAL_ORDER_ON_INTERVENTION_FLAG_NAME,
  AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT_FLAG_NAME,
  AUTO_SUBMIT_AI_AUTO_MODE_BYPASS_FLAG_NAME,
  AI_SUGGESTION_AUTO_ADD_FLAG_NAME,
  HITL_7_0_FLAG_NAME,
  HITL_7_CENTER_PANE_FLAG_NAME,
  SHADOW_MODE_FLAG_NAME,
} from '../../redux/features/config/config.flags';

const useUnleashHooks = () => {
  const audioBackend = useFlag('audio_backend');
  const autoCombo = useFlag('auto_combo');
  const bmAutoCombo = useFlag('bm_auto_combo');
  const authRefactor = useFlag('auth_refactor');
  const agentInactivityRefactor = useFlag('agent_inactivity_refactor');
  const finalOrderValidation = useFlag('final_order_validation');
  const posCart = useFlag('pos_cart');
  const loyaltySupport = useFlag('loyalty_support');
  const itemConversion = useFlag('item_conversion');
  const notifyHitlBuild = useFlag('notify_hitl_build');
  const useMenuModuleForOrderSubmission = useFlag(
    'use_menu_module_for_order_submission'
  );
  const sessionEndOrderActionChanges = useFlag(
    SESSION_END_ORDER_ACTION_CHANGES_FLAG_NAME
  );
  const timerResetOnCartUpdate = useFlag(TIMER_RESET_ON_CART_UPDATE_FLAG_NAME);
  const suppressUpsell = useFlag(SUPPRESS_UPSELL_FLAG_NAME);
  const doNotUpsellInExpressMode = useFlag(
    DO_NOT_UPSELL_IN_EXPRESS_MODE_FLAG_NAME
  );
  const unresolvedItems = useFlag(UNRESOLVED_ITEMS_FLAG_NAME);
  const hitl3 = useFlag(HITL_3_0_FLAG_NAME);
  const agentCart = useFlag(AGENT_CART_FLAG_NAME);
  const expressTimer = useFlag(HITL_EXPRESS_TIMER_FLAG_NAME);
  const menuModuleErrorUi = useFlag(MENU_MODULE_ERROR_UI_FLAG_NAME);
  const orderErrorOmnibarPrompt = useFlag(ORDER_ERROR_OMNIBAR_PROMPT_FLAG_NAME);
  const secondaryAsrEnabled = useFlag(SECONDARY_ASR_ENABLED_FLAG_NAME);
  // The `hitl_express_timer` flag carries a `max_seconds` variant whose payload
  // value is the order-time threshold (seconds). 0 / missing => no threshold.
  const expressTimerVariant = useVariant(HITL_EXPRESS_TIMER_FLAG_NAME);
  const expressTimerMaxSeconds =
    parseInt(expressTimerVariant?.payload?.value ?? '', 10) || 0;
  // The `agent_response_time` flag carries a variant whose payload value is the
  // response-time SLA (seconds). Falls back to the default when the flag/variant
  // is absent, preserving the previous env-var default (6s).
  const agentResponseTimeVariant = useVariant(AGENT_RESPONSE_TIME_FLAG_NAME);
  const agentResponseTimeLimitSeconds =
    parseInt(agentResponseTimeVariant?.payload?.value ?? '', 10) ||
    DEFAULT_AGENT_RESPONSE_TIME_LIMIT_SECONDS;

  // ON/OFF ONLY. The taxonomy is hardcoded in src/constants/issueTypes.ts — there is
  // deliberately no useVariant() here (HITL-1501). Every category is bound to a chip
  // colour and a display label, so a config-supplied type would render uncoloured and
  // unlabelled.
  const issueReporting = useFlag(ISSUE_REPORTING_FLAG_NAME);

  // HITL-1551 kill switch for the background autofill + final submit on staff
  // intervention. On/off only — the fill has nothing tunable.
  const autofillFinalOrderOnIntervention = useFlag(
    AUTOFILL_FINAL_ORDER_ON_INTERVENTION_FLAG_NAME
  );

  // HITL-1562 kill switch for the auto-submit autofill (post-car_exit timer
  // expiry). On/off only, independent of the intervention flag above.
  const autofillFinalOrderOnAutoSubmit = useFlag(
    AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT_FLAG_NAME
  );

  // HITL-1607 kill switch: bypass sendOrder's isAIAutoMode guard on the
  // auto-submit paths.
  const autoSubmitAIAutoModeBypass = useFlag(
    AUTO_SUBMIT_AI_AUTO_MODE_BYPASS_FLAG_NAME
  );

  // HITL-1429: accepting an AI post-readback upsell suggestion also adds the
  // item to the agent cart. On/off only — there is nothing tunable about it.
  const aiSuggestionAutoAdd = useFlag(AI_SUGGESTION_AUTO_ADD_FLAG_NAME);

  // HITL 7.0 console refinements. On/off only — the deltas have nothing
  // tunable. Consumers read selectIsHitl7Enabled, never this flag alone.
  const hitl7 = useFlag(HITL_7_0_FLAG_NAME);

  // HITL-1603: the reworked centre pane (one row per modifier group, options
  // in a drawer). On/off only - there is nothing tunable about the layout.
  // Consumers read selectIsHitl7CenterPaneEnabled, never this flag alone.
  const hitl7CenterPane = useFlag(HITL_7_CENTER_PANE_FLAG_NAME);

  // HITL-1604: supervised AI mode — the Shadow / Intercept switch and the
  // silence rule behind it. On/off only; which mode the agent is in is
  // agent state. Consumers read selectIsShadowModeEnabled, never this alone.
  const shadowMode = useFlag(SHADOW_MODE_FLAG_NAME);

  const orderCheckRetry = useFlag(ORDER_CHECK_RETRY_FLAG_NAME);
  const maskOrderPendingError = useFlag(MASK_ORDER_PENDING_ERROR_FLAG_NAME);
  // The `order_check_retry` flag carries a variant whose payload value is the
  // check-event timeout (seconds) before the Retry button is offered.
  const orderCheckRetryVariant = useVariant(ORDER_CHECK_RETRY_FLAG_NAME);
  const orderCheckTimeoutSeconds =
    parseInt(orderCheckRetryVariant?.payload?.value ?? '', 10) ||
    DEFAULT_ORDER_CHECK_TIMEOUT_SECONDS;
  const orderStatusBanner = useFlag(ORDER_STATUS_BANNER_FLAG_NAME);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_AUDIO_BACKEND: audioBackend }));
  }, [audioBackend]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_AUTO_COMBO: autoCombo }));
  }, [autoCombo]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_BM_AUTO_COMBO: bmAutoCombo }));
  }, [bmAutoCombo]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_LOYALTY_SUPPORT: loyaltySupport }));
  }, [loyaltySupport]);

  useEffect(() => {
    setAgentInactivityFeatureFlag((!!agentInactivityRefactor).toString());
  }, [agentInactivityRefactor]);

  useEffect(() => {
    setAuthRefactorFeatureFlag((!!authRefactor).toString());
  }, [authRefactor]);

  useEffect(() => {
    dispatch(
      update({ FEATURE_FLAG_FINAL_ORDER_VALIDATION: finalOrderValidation })
    );
  }, [finalOrderValidation]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_POS_CART: posCart }));
  }, [posCart]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_ITEM_CONVERSION: itemConversion }));
  }, [itemConversion, dispatch]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_NOTIFY_HITL_BUILD: notifyHitlBuild }));
  }, [notifyHitlBuild]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION:
          useMenuModuleForOrderSubmission,
      })
    );
  }, [useMenuModuleForOrderSubmission]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_SESSION_END_ORDER_ACTION_CHANGES:
          sessionEndOrderActionChanges,
      })
    );
  }, [sessionEndOrderActionChanges]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_TIMER_RESET_ON_CART_UPDATE: timerResetOnCartUpdate,
      })
    );
  }, [timerResetOnCartUpdate]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsell }));
  }, [suppressUpsell]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE: doNotUpsellInExpressMode,
      })
    );
  }, [doNotUpsellInExpressMode]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_UNRESOLVED_ITEMS: unresolvedItems }));
  }, [unresolvedItems]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_HITL_3_0: hitl3 }));
  }, [hitl3]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_AGENT_CART: agentCart }));
  }, [agentCart]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_HITL_EXPRESS_TIMER: expressTimer,
        HITL_EXPRESS_TIMER_MAX_SECONDS: expressTimerMaxSeconds,
      })
    );
  }, [expressTimer, expressTimerMaxSeconds]);

  useEffect(() => {
    dispatch(
      update({
        AGENT_RESPONSE_TIME_LIMIT_SECONDS: agentResponseTimeLimitSeconds,
      })
    );
  }, [agentResponseTimeLimitSeconds]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_MENU_MODULE_ERROR_UI: menuModuleErrorUi }));
  }, [menuModuleErrorUi]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_ORDER_ERROR_OMNIBAR_PROMPT: orderErrorOmnibarPrompt,
      })
    );
  }, [orderErrorOmnibarPrompt]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_ORDER_CHECK_RETRY: orderCheckRetry,
        ORDER_CHECK_TIMEOUT_SECONDS: orderCheckTimeoutSeconds,
      })
    );
  }, [orderCheckRetry, orderCheckTimeoutSeconds]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_ORDER_STATUS_BANNER: orderStatusBanner }));
  }, [orderStatusBanner]);

  useEffect(() => {
    dispatch(
      update({ FEATURE_FLAG_MASK_ORDER_PENDING_ERROR: maskOrderPendingError })
    );
  }, [maskOrderPendingError]);

  useEffect(() => {
    dispatch(
      update({ FEATURE_FLAG_SECONDARY_ASR_ENABLED: secondaryAsrEnabled })
    );
  }, [secondaryAsrEnabled]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_ISSUE_REPORTING: issueReporting,
      })
    );
  }, [issueReporting]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION:
          autofillFinalOrderOnIntervention,
      })
    );
  }, [autofillFinalOrderOnIntervention]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT:
          autofillFinalOrderOnAutoSubmit,
      })
    );
  }, [autofillFinalOrderOnAutoSubmit]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_AUTO_SUBMIT_AI_AUTO_MODE_BYPASS:
          autoSubmitAIAutoModeBypass,
      })
    );
  }, [autoSubmitAIAutoModeBypass]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_AI_SUGGESTION_AUTO_ADD: aiSuggestionAutoAdd,
      })
    );
  }, [aiSuggestionAutoAdd]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_HITL_7_0: hitl7,
      })
    );
  }, [hitl7]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_HITL_7_CENTER_PANE: hitl7CenterPane,
      })
    );
  }, [hitl7CenterPane]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_SHADOW_MODE: shadowMode,
      })
    );
  }, [shadowMode]);
};

export default useUnleashHooks;
